export type SkyspireTemplateCode = 'A' | 'B' | 'C' | 'D' | 'E';

export interface SkyspirePoint {
  x: number;
  y: number;
}

export interface SkyspireTemplate {
  points: SkyspirePoint[];
  edges: Array<[number, number]>;
  linePoints: SkyspirePoint[][];
}

export interface SkyspireResponsiveTemplate {
  pc: SkyspireTemplate;
  phone: SkyspireTemplate;
}

const edgesA: Array<[number, number]> = [
  [0, 1],
  [0, 2],
  [0, 4],
  [1, 3],
  [2, 5],
  [4, 5],
  [3, 6],
  [1, 6],
  [5, 7],
  [6, 7],
  [7, 9],
];
const edgesB: Array<[number, number]> = [
  [0, 7],
  [2, 3],
  [3, 9],
  [4, 5],
  [5, 6],
  [6, 7],
];
const edgesC: Array<[number, number]> = [
  [0, 1],
  [0, 4],
  [1, 9],
  [2, 8],
  [2, 9],
  [3, 4],
  [5, 6],
  [6, 7],
];
const edgesD: Array<[number, number]> = [
  [0, 1],
  [0, 5],
  [1, 2],
  [2, 3],
  [3, 6],
  [4, 5],
  [4, 8],
  [5, 9],
  [6, 7],
  [8, 9],
];
const edgesE: Array<[number, number]> = [
  [0, 1],
  [0, 6],
  [1, 2],
  [2, 3],
  [3, 4],
  [3, 7],
  [4, 5],
  [5, 6],
  [5, 9],
  [7, 8],
  [8, 9],
  [1, 5],
];

// Intermediate path points are also the positions of the decorative stars.
// Keeping them explicit allows a reference path to have zero, one, or multiple bends.
const linePointsA: SkyspirePoint[][] = [
  [{ x: 35, y: 20 }],
  [
    { x: 62, y: 13 },
    { x: 65, y: 17 },
  ],
  [{ x: 52.3, y: 25 }],
  [{ x: 19, y: 32 }],
  [{ x: 74, y: 28 }],
  [{ x: 65, y: 40 }],
  [{ x: 20, y: 45 }],
  [
    { x: 35, y: 20 },
    { x: 36.6, y: 31.9 },
  ],
  [{ x: 65, y: 40 }],
  [
    { x: 41, y: 63 },
    { x: 53.6, y: 69.9 },
  ],
  // [],
  [{ x: 53.6, y: 69.9 }],
];

const linePointsB: SkyspirePoint[][] = [
  [
    { x: 60, y: 7 },
    { x: 60, y: 10 },
  ],
  [
    { x: 32, y: 36 },
    { x: 32, y: 47 },
  ],
  [
    { x: 38, y: 62 },
    { x: 38, y: 74 },
    { x: 48, y: 80 },
  ],
  [
    { x: 54, y: 70 },
    { x: 60, y: 65 },
    { x: 60, y: 60 },
  ],
  [
    { x: 75, y: 57 },
    { x: 73, y: 45 },
  ],
  [{ x: 81, y: 27 }],
];

const linePointsC: SkyspirePoint[][] = [
  [{ x: 24.5, y: 27 }],
  [{ x: 38, y: 12 }],
  [{ x: 38, y: 48 }],
  [
    { x: 60, y: 43 },
    { x: 60, y: 48 },
    { x: 70, y: 53 },
  ],
  [
    { x: 60, y: 43 },
    { x: 60, y: 48 },
  ],
  [
    { x: 73, y: 22 },
    { x: 66, y: 10 },
  ],
  [{ x: 19, y: 70 }],
  [{ x: 38, y: 80 }],
];

const linePointsD: SkyspirePoint[][] = [
  [{ x: 58, y: 18 }],
  [
    { x: 80, y: 17 },
    { x: 78, y: 28 },
  ],
  [{ x: 28, y: 23 }],
  [],
  [
    { x: 23, y: 61 },
    { x: 20, y: 68 },
  ],
  [{ x: 68, y: 47 }],
  [{ x: 51, y: 63 }],
  [{ x: 77, y: 56 }],
  [{ x: 25, y: 90 }],
  // [],
  [{ x: 63, y: 72 }],
];

const linePointsE: SkyspirePoint[][] = [
  [{ x: 45, y: 11 }],
  [{ x: 68, y: 17 }],
  [{ x: 28, y: 15 }],
  [{ x: 20, y: 40 }],
  [{ x: 40, y: 53 }],
  [{ x: 23, y: 63 }],
  [],
  [{ x: 78, y: 32 }],
  [{ x: 78, y: 60 }],
  [{ x: 32, y: 85 }],
  [{ x: 69, y: 88 }],
  [
    { x: 55, y: 25 },
    { x: 58, y: 35 },
  ],
];

function mapLinePointsForPhone(linePoints: SkyspirePoint[][], pcPoints: SkyspirePoint[], phonePoints: SkyspirePoint[]): SkyspirePoint[][] {
  return linePoints.map((edgePoints) =>
    edgePoints.map((point) => {
      let weightTotal = 0;
      let deltaX = 0;
      let deltaY = 0;

      pcPoints.forEach((pcPoint, index) => {
        const distanceSquared = (point.x - pcPoint.x) ** 2 + (point.y - pcPoint.y) ** 2;
        const weight = 1 / Math.max(distanceSquared, 1);
        weightTotal += weight;
        deltaX += (phonePoints[index].x - pcPoint.x) * weight;
        deltaY += (phonePoints[index].y - pcPoint.y) * weight;
      });

      return {
        x: Number((point.x + deltaX / weightTotal).toFixed(1)),
        y: Number((point.y + deltaY / weightTotal).toFixed(1)),
      };
    })
  );
}

export const skyspireTemplates: Record<SkyspireTemplateCode, SkyspireResponsiveTemplate> = {
  A: {
    pc: {
      points: [
        { x: 51, y: 8 },
        { x: 24, y: 18 },
        { x: 74, y: 16 },
        { x: 14, y: 39 },
        { x: 53, y: 35 },
        { x: 74, y: 39 },
        { x: 29, y: 53 },
        { x: 64, y: 65 },
        { x: 20, y: 76 },
        { x: 51, y: 85 },
      ],
      edges: edgesA,
      linePoints: linePointsA,
    },
    phone: {
      points: [
        { x: 49, y: 9 },
        { x: 25, y: 18 },
        { x: 69, y: 16 },
        { x: 16, y: 37 },
        { x: 52, y: 33 },
        { x: 68, y: 37 },
        { x: 29, y: 49 },
        { x: 59, y: 60 },
        { x: 22, y: 70 },
        { x: 49, y: 78 },
      ],
      edges: edgesA,
      linePoints: linePointsA,
    },
  },
  B: {
    pc: {
      points: [
        { x: 48, y: 7 },
        { x: 36, y: 18 },
        { x: 24, y: 32 },
        { x: 29, y: 56 },
        { x: 48, y: 63 },
        { x: 68, y: 55 },
        { x: 81, y: 40 },
        { x: 71, y: 14 },
        { x: 34, y: 78 },
        { x: 61, y: 80 },
      ],
      edges: edgesB,
      linePoints: linePointsB,
    },
    phone: {
      points: [
        { x: 46, y: 8 },
        { x: 36, y: 17 },
        { x: 26, y: 30 },
        { x: 30, y: 52 },
        { x: 46, y: 58 },
        { x: 63, y: 51 },
        { x: 74, y: 38 },
        { x: 65, y: 14 },
        { x: 34, y: 71 },
        { x: 56, y: 73 },
      ],
      edges: edgesB,
      linePoints: linePointsB,
    },
  },
  C: {
    pc: {
      points: [
        { x: 23, y: 10 },
        { x: 30, y: 34 },
        { x: 63, y: 31 },
        { x: 80, y: 17 },
        { x: 51, y: 5 },
        { x: 14, y: 54 },
        { x: 24, y: 78 },
        { x: 55, y: 82 },
        { x: 76, y: 65 },
        { x: 43, y: 56 },
      ],
      edges: edgesC,
      linePoints: linePointsC,
    },
    phone: {
      points: [
        { x: 24, y: 11 },
        { x: 30, y: 31 },
        { x: 58, y: 29 },
        { x: 73, y: 17 },
        { x: 48, y: 6 },
        { x: 17, y: 49 },
        { x: 24, y: 71 },
        { x: 49, y: 75 },
        { x: 68, y: 59 },
        { x: 40, y: 51 },
      ],
      edges: edgesC,
      linePoints: linePointsC,
    },
  },
  D: {
    pc: {
      points: [
        { x: 69, y: 8 },
        { x: 43, y: 16 },
        { x: 18, y: 27 },
        { x: 29, y: 48 },
        { x: 56, y: 51 },
        { x: 81, y: 42 },
        { x: 17, y: 74 },
        { x: 39, y: 92 },
        { x: 47, y: 71 },
        { x: 75, y: 66 },
      ],
      edges: edgesD,
      linePoints: linePointsD,
    },
    phone: {
      points: [
        { x: 63, y: 9 },
        { x: 41, y: 16 },
        { x: 20, y: 26 },
        { x: 29, y: 45 },
        { x: 52, y: 47 },
        { x: 73, y: 39 },
        { x: 18, y: 67 },
        { x: 37, y: 84 },
        { x: 44, y: 65 },
        { x: 68, y: 61 },
      ],
      edges: edgesD,
      linePoints: linePointsD,
    },
  },
  E: {
    pc: {
      points: [
        { x: 56, y: 6 },
        { x: 39, y: 14 },
        { x: 18, y: 24 },
        { x: 30, y: 47 },
        { x: 55, y: 59 },
        { x: 75, y: 43 },
        { x: 82, y: 17 },
        { x: 18, y: 76 },
        { x: 49, y: 85 },
        { x: 82, y: 80 },
      ],
      edges: edgesE,
      linePoints: linePointsE,
    },
    phone: {
      points: [
        { x: 52, y: 7 },
        { x: 38, y: 14 },
        { x: 20, y: 22 },
        { x: 30, y: 43 },
        { x: 51, y: 54 },
        { x: 68, y: 40 },
        { x: 74, y: 16 },
        { x: 20, y: 69 },
        { x: 46, y: 77 },
        { x: 74, y: 73 },
      ],
      edges: edgesE,
      linePoints: linePointsE,
    },
  },
};

Object.values(skyspireTemplates).forEach(({ pc, phone }) => {
  phone.linePoints = mapLinePointsForPhone(pc.linePoints, pc.points, phone.points);
});

const templateCodes: SkyspireTemplateCode[] = ['A', 'B', 'C', 'D', 'E'];

export function getSkyspireTemplateCode(page: number): SkyspireTemplateCode {
  return templateCodes[(Math.max(1, page) - 1) % templateCodes.length];
}
